import {Scheduler} from "@measure-up/rhythm";
import {ClickPlayer} from "../../shared/click-player";
import {NoteVoice} from "./note-voice";
import {PhraseCycle} from "./phrase-cycle";
import {createPhraserEventSource} from "./phraser-event-source";
import {countInSeed, PhraserEvent} from "./phraser-events";

interface PhraserPlayerConfig {
    bpm: number;
    beatsPerBar: number;
    /** Count-in bars before the first phrase, 0..4. */
    countInBars: number;
    hasClickTrack: boolean;
}

/**
 * PhraserPlayer - wires the merged event stream to the kernel Scheduler
 * and dispatches scheduled events to the audio seams.
 *
 * One Scheduler<PhraserEvent>: the merged stream makes a single
 * tempo-conversion point (bpm is live-read per scheduled event), so clicks
 * and notes can never diverge on live tempo changes. currentTime comes from
 * the shared AudioContext (via ClickPlayer), so clicks and notes schedule
 * against the same clock.
 *
 * Count-in is the scheduler's seed: countInBars bars of ordinary click
 * bars. The cycle is untouched by it - PhraseCycle's first pulled batch is
 * a phrase bar, landing immediately after the count-in.
 *
 * Restart semantics: stop/start re-plays the count-in and restarts the
 * cycle on a phrase bar (phraseCycle.reset() on stop, consistent with
 * Clicker's clean restart at a downbeat).
 *
 * All config fields are live-read: bpm per scheduled event (the
 * scheduler's gap conversion and dispatch's note-length conversion, ADR
 * 0003), the rest at the next bar boundary.
 */
class PhraserPlayer {

    private readonly config: PhraserPlayerConfig;
    private readonly phraseCycle: PhraseCycle;
    private readonly clickPlayer: ClickPlayer;
    private readonly noteVoice: NoteVoice;
    private readonly scheduler: Scheduler<PhraserEvent>;

    constructor(config: PhraserPlayerConfig, phraseCycle: PhraseCycle, clickPlayer: ClickPlayer, noteVoice: NoteVoice) {
        this.config = config;
        this.phraseCycle = phraseCycle;
        this.clickPlayer = clickPlayer;
        this.noteVoice = noteVoice;

        this.scheduler = new Scheduler<PhraserEvent>(
            {
                bpm: () => this.config.bpm,
                source: createPhraserEventSource(this.config, this.phraseCycle),
            },
            () => this.clickPlayer.currentTime,
        );

        this.scheduler.events$.subscribe(({event, timestamp}) => {
            if (event.click !== undefined) {
                this.clickPlayer.click(timestamp, event.click);
            }
            if (event.note !== undefined) {
                // beats -> seconds at dispatch, not in the voice (ADR 0003):
                // the dispatch is synchronous inside the scheduler's
                // scheduling pass, so this bpm read coincides with the
                // scheduler's own gap conversion - the single
                // tempo-conversion-point property survives. The voice stays
                // purely in the audio domain: seconds and timestamps.
                this.noteVoice.play(event.note.pitch, timestamp, event.note.length * 60 / this.config.bpm);
            }
        });
    }

    /** Start playback, with the count-in as the seed. */
    start() {
        this.scheduler.start(countInSeed(this.config.countInBars, this.config.beatsPerBar, this.config.hasClickTrack));
    }

    /** Stop playback, silence scheduled audio, and reset the cycle to a phrase bar. */
    stop() {
        this.scheduler.stop();
        this.clickPlayer.stop();
        this.noteVoice.stop();
        this.phraseCycle.reset();
    }
}

export {PhraserPlayer, PhraserPlayerConfig};
